import { useEffect, useState } from 'react'
import { fetchVote, castVote } from '../api/votes'
import Icon from './Icon'
import ConfirmDialog from './ConfirmDialog'
import styles from './VoteButtons.module.css'

/**
 * VoteButtons — generic up/down vote control. Shared per tenant, not per user.
 *
 * Props:
 *   subjectType {string} — e.g. 'ticket_skill', 'response_plan'
 *   ticketId, skillId — real ids identifying the subject (which fields apply
 *     depends on subjectType)
 *   subjectName, subjectDescription {string} — display copies stored alongside
 *     the ids, not used for matching
 *   confirmDown {{ title?, message }} — [optional] show a ConfirmDialog with
 *     this copy before a down vote is cast
 *   onVoted {(direction: 1|-1) => void} — [optional] called after a vote is
 *     cast (not when cleared)
 */
export default function VoteButtons({
  subjectType, ticketId, skillId, subjectName, subjectDescription,
  confirmDown, onVoted,
}) {
  const [vote, setVote] = useState(0)
  const [busy, setBusy] = useState(false)
  const [pendingDown, setPendingDown] = useState(false)
  const hasSubject = subjectType && (ticketId != null || skillId != null)

  useEffect(() => {
    let active = true
    setVote(0)
    setPendingDown(false)
    if (!hasSubject) return
    fetchVote(subjectType, { ticketId, skillId })
      .then(v => { if (active) setVote(v.vote) })
      .catch(() => { /* best-effort — leave at the neutral 0 default */ })
    return () => { active = false }
  }, [subjectType, ticketId, skillId, hasSubject])

  async function castDirection(direction) {
    if (busy || !hasSubject) return
    const previous = vote
    setBusy(true)
    setVote(previous === direction ? 0 : direction) // optimistic
    try {
      const result = await castVote(subjectType, { ticketId, skillId }, subjectName, subjectDescription, direction)
      setVote(result.vote)
      if (result.vote !== 0) onVoted?.(direction)
    } catch {
      setVote(previous) // roll back on failure
    } finally {
      setBusy(false)
    }
  }

  function handleDownClick() {
    if (busy || !hasSubject) return
    if (confirmDown) setPendingDown(true)
    else castDirection(-1)
  }

  function confirmDownvote() {
    setPendingDown(false)
    castDirection(-1)
  }

  return (
    <div className={styles.voteButtons} role="group" aria-label="Vote">
      <button
        type="button"
        className={[
          styles.voteBtn,
          vote === 1 ? styles.voteBtnUp : '',
          vote === -1 ? styles.voteBtnDimmed : '',
        ].filter(Boolean).join(' ')}
        disabled={busy}
        aria-pressed={vote === 1}
        title="Vote up"
        onClick={() => castDirection(1)}
      >
        <Icon name="thumb" />
      </button>
      <button
        type="button"
        className={[
          styles.voteBtn,
          vote === -1 ? styles.voteBtnDown : '',
          vote === 1 ? styles.voteBtnDimmed : '',
        ].filter(Boolean).join(' ')}
        disabled={busy}
        aria-pressed={vote === -1}
        title="Vote down"
        onClick={handleDownClick}
      >
        <Icon name="thumb" className={styles.thumbDown} />
      </button>

      {pendingDown && (
        <ConfirmDialog
          title={confirmDown.title ?? 'Down vote this?'}
          message={confirmDown.message}
          confirmLabel="Down vote"
          variant="danger"
          onConfirm={confirmDownvote}
          onCancel={() => setPendingDown(false)}
        />
      )}
    </div>
  )
}
